<template>
  <v-container class="special-files-admin">
    <h1 class="form-title mb-4">Special File Management</h1>

    <v-data-table
      :headers="headers"
      :items="specialFiles"
      :loading="loadingFiles"
      item-value="id"
      data-testid="special-files-table"
    >
      <template #[`item.is_visible`]="{ item }">
        <v-chip :color="item.is_visible ? 'green' : 'grey'" size="small">
          {{ item.is_visible ? 'Visible' : 'Hidden' }}
        </v-chip>
      </template>
      <template #[`item.released_on`]="{ item }">
        {{ formatDate(item.released_on) }}
      </template>
      <template #[`item.actions`]="{ item }">
        <v-btn
          size="small"
          variant="text"
          icon
          aria-label="Upload new version"
          @click="openUploadForFile(item)"
          data-testid="edit-btn"
        >
          <v-icon>fas fa-pen</v-icon>
        </v-btn>
      </template>
    </v-data-table>

    <v-dialog v-model="uploadDialog" max-width="600" persistent>
      <v-card>
        <v-card-title>Upload New Version — {{ newFile.app }}</v-card-title>
        <v-card-text>
          <div v-if="!uploadSuccess">
            <v-form @submit.prevent="submitForm" ref="form">
              <v-row>
                <v-col cols="6">
                  <v-text-field
                    v-model="newFile.version"
                    label="Version"
                    :rules="[rules.required]"
                    required
                    variant="outlined"
                    color="primary"
                    data-testid="version-input"
                  ></v-text-field>
                </v-col>
                <v-col cols="6">
                  <v-text-field
                    v-model="newFile.released_on"
                    label="Released On"
                    type="date"
                    :rules="[rules.required]"
                    required
                    variant="outlined"
                    color="primary"
                    data-testid="released-on-input"
                  ></v-text-field>
                </v-col>
              </v-row>

              <v-file-input
                v-model="selectedFile"
                label="Special File"
                :rules="[rules.fileRequired]"
                required
                variant="outlined"
                color="primary"
                accept=".zip"
                show-size
                counter
                :multiple="false"
                data-testid="file-input"
              ></v-file-input>

              <v-switch
                v-model="newFile.is_visible"
                label="Is Visible"
                color="primary"
                density="compact"
                data-testid="is-visible-switch"
              ></v-switch>

              <v-alert
                v-if="errorMessage"
                type="error"
                class="mt-3"
                closable
                variant="tonal"
                data-testid="error-alert"
              >{{ errorMessage }}</v-alert>
            </v-form>
          </div>
          <div v-else class="text-center py-4">
            <v-icon size="64" color="green">fas fa-check-circle</v-icon>
            <h2 class="my-4">Special File Updated Successfully!</h2>
          </div>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <template v-if="!uploadSuccess">
            <v-btn @click="closeDialog" :disabled="loading" data-testid="cancel-btn">Cancel</v-btn>
            <v-btn
              class="str-btn-primary"
              @click="submitForm"
              :loading="loading"
              data-testid="submit-btn"
            >
              Upload
            </v-btn>
          </template>
          <template v-else>
            <v-btn class="str-btn-primary" @click="closeDialog" data-testid="done-btn">Done</v-btn>
          </template>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script setup>
import { onMounted, reactive, ref } from 'vue';
import { useCBERequest } from '@/composables';

const { makeCBERequest } = useCBERequest();

const specialFiles = ref([]);
const loadingFiles = ref(false);
const uploadDialog = ref(false);
const uploadSuccess = ref(false);
const loading = ref(false);
const errorMessage = ref('');
const selectedFile = ref(null);
const selectedFileId = ref(null);
const form = ref(null);

const newFile = reactive({
  app: '',
  version: '',
  released_on: '',
  is_visible: true,
  url: '',
});

const headers = [
  { title: 'App', key: 'app' },
  { title: 'Version', key: 'version' },
  { title: 'Released On', key: 'released_on' },
  { title: 'Auth0 Permission', key: 'auth0_permission' },
  { title: 'Visible', key: 'is_visible' },
  { title: '', key: 'actions', sortable: false },
];

const rules = {
  required: value => !!value || 'Required.',
  fileRequired: value => !!value || 'File is required.',
};

const formatDate = (dateStr) => {
  if (!dateStr) return '';
  const [year, month, day] = dateStr.split('T')[0].split('-').map(Number);
  return new Date(year, month - 1, day).toLocaleDateString();
};

const openUploadForFile = (file) => {
  resetForm();
  selectedFileId.value = file.id;
  newFile.app = file.app;
  newFile.is_visible = file.is_visible;
  newFile.released_on = file.released_on ? file.released_on.split('T')[0] : '';
  uploadDialog.value = true;
};

const fetchSpecialFiles = async () => {
  loadingFiles.value = true;
  try {
    specialFiles.value = await makeCBERequest('/piccolo/specialfiles', 'get') ?? [];
  } finally {
    loadingFiles.value = false;
  }
};

const resetForm = () => {
  Object.assign(newFile, {
    app: '',
    version: '',
    released_on: '',
    is_visible: true,
    url: '',
  });
  selectedFile.value = null;
  selectedFileId.value = null;
  uploadSuccess.value = false;
  errorMessage.value = '';
  if (form.value) form.value.resetValidation();
};

const closeDialog = () => {
  const shouldRefresh = uploadSuccess.value;
  uploadDialog.value = false;
  resetForm();
  if (shouldRefresh) fetchSpecialFiles();
};

const submitForm = async () => {
  errorMessage.value = '';
  const { valid } = await form.value.validate();

  if (!valid) {
    errorMessage.value = 'Please correct the form errors.';
    return;
  }

  loading.value = true;
  try {
    const canonicalFilename = `${newFile.app}-${newFile.version}.zip`;
    const renamedFile = new File([selectedFile.value], canonicalFilename, {
      type: selectedFile.value.type,
    });

    const formData = new FormData();
    formData.append('file', renamedFile);

    const uploadResult = await makeCBERequest(
      '/piccolo/specialfiles/upload', 'post', formData
    );
    if (uploadResult === undefined) return;
    if (!uploadResult.s3_url) {
      errorMessage.value = 'File upload succeeded but no URL was returned.';
      return;
    }

    newFile.url = uploadResult.s3_url;

    const patchResult = await makeCBERequest(
      `/piccolo/specialfiles/${selectedFileId.value}`,
      'patch',
      {
        version: newFile.version,
        url: newFile.url,
        released_on: newFile.released_on,
        is_visible: newFile.is_visible,
      }
    );
    if (patchResult === undefined) return;

    uploadSuccess.value = true;
  } finally {
    loading.value = false;
  }
};

onMounted(fetchSpecialFiles);
</script>

<style scoped lang="scss">
  @import '@/assets/styles/StrVariables.scss';
  @import '@/assets/styles/StrButtons.scss';
</style>
